import {Permissions, RoleData} from "@/enums/user";
import {GET_GAIL_PATH, shouldRedirectToGetGail} from "@/utils/common/constants/routes";
import {matchRouteGateEntry, permissionGateLevels} from "@/utils/authz/routeGates";

export const UNAUTHORIZED_PATH = "/unauthorized";
export const DASHBOARD_PATH = "/";
export const GAILGPT_PATH = "/gailgpt";
export {GET_GAIL_PATH};

/**
 * `skip` means we deliberately do not have a trustworthy answer and must not act on one. It is
 * distinct from `allow` so the caller cannot collapse "permitted" and "unknown" into the same
 * branch — locking users out during a Galactus outage would be worse than briefly over-showing.
 *
 * `unknown` is the opposite case and denies: it means the gate table failed to answer, which the
 * coverage test makes impossible for a real page, so it stays separate from `block` only to carry a
 * reason worth logging.
 *
 * `redirect` is a landing fallback, not a denial. Login's default `returnTo` is Dashboard, which
 * asks for `call.read`. A GPT-only caller holds `conversation.read` instead, so sending them to
 * `/unauthorized` is the wrong answer — they have a product home, just not this one. Other denied
 * routes still `block`.
 */
export type RouteAccessDecision =
  | {verdict: "allow"}
  | {verdict: "block"}
  | {verdict: "redirect"; to: string}
  | {verdict: "skip"; reason: string}
  | {verdict: "unknown"; reason: string};

export type RouteAccessInput = {
  pathname: string;
  isAuthenticated: boolean;
  /** JWT org claim from the seed. Empty means the user still needs /get-gail. */
  agencyId: string | undefined | null;
  /** The seed fell back to the empty matrix; every check would deny. */
  permissionsDegraded: boolean;
  /** First-login Galactus provisioning has not landed, so scopes are still arriving. */
  provisioningPending: boolean;
  /** Null when the seed carried no matrix at all. */
  hasPermissions: boolean;
  canAccessAny: (dataTypes: readonly RoleData[], level: Permissions) => boolean;
};

/** Decides whether a client-side navigation to `pathname` is permitted by the route gate table. */
export function decideRouteAccess({
  pathname,
  isAuthenticated,
  agencyId,
  permissionsDegraded,
  provisioningPending,
  hasPermissions,
  canAccessAny,
}: RouteAccessInput): RouteAccessDecision {
  if (!isAuthenticated) return {verdict: "skip", reason: "unauthenticated"};
  if (shouldRedirectToGetGail(pathname, agencyId)) {
    return {verdict: "redirect", to: GET_GAIL_PATH};
  }
  if (permissionsDegraded) return {verdict: "skip", reason: "permissions degraded"};
  if (provisioningPending) return {verdict: "skip", reason: "provisioning pending"};
  if (!hasPermissions) return {verdict: "skip", reason: "no permission matrix in the seed"};
  // Redirecting the denial page to itself would spin.
  if (pathname === UNAUTHORIZED_PATH) return {verdict: "skip", reason: "already unauthorized"};

  const entry = matchRouteGateEntry(pathname);
  if (!entry) return {verdict: "unknown", reason: `no route gate matches ${pathname}`};
  const {pattern, gate} = entry;
  if (gate.kind === "exempt") return {verdict: "allow"};

  const granted = permissionGateLevels(gate).some((level) => canAccessAny(gate.dataTypes, level));
  if (granted) return {verdict: "allow"};

  // Login's default returnTo is `/`. Dashboard still requires `call.read`; this only picks a
  // better home when the caller can actually open GailGPT.
  if (pattern === DASHBOARD_PATH && canAccessAny([RoleData.GPT], Permissions.Read)) {
    return {verdict: "redirect", to: GAILGPT_PATH};
  }

  return {verdict: "block"};
}
